import { useState } from 'react';

import {
  Box,
  Card,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import Navigation from '@/karehero-app/shell/Navigation';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import {
  ASSESS_ROWS,
  CARE_PROFILE,
  PROFILE_CALLS,
  PROFILE_DOCS,
} from '@/karehero-app/tell-us-once/intake/content';

const SCREEN_PX = 2;

const TABS = [
  { id: 'assess', label: 'Assess' },
  { id: 'about', label: 'About' },
  { id: 'docs', label: 'Docs' },
  { id: 'calls', label: 'Calls' },
] as const;

export type TabId = (typeof TABS)[number]['id'];

// A document or a call note, as one row. The same row shape for both: they are
// the same kind of thing to read, even though they are found under their own
// pill.
const RecordRow = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <Card sx={{ p: 2 }}>
    <Stack spacing={0.25}>
      <Typography variant='h6'>{title}</Typography>
      <Typography sx={{ color: 'text.secondary' }} variant='body2'>
        {subtitle}
      </Typography>
    </Stack>
  </Card>
);

// One of the four lists, on its own: what a pill shows here, and what a section
// of the care hub opens into in the architecture concept that folds Profile
// into Care.
export const ProfileSection = ({
  tab,
  onCard,
}: {
  tab: TabId;
  onCard?: () => void;
}) => (
  <Stack spacing={1.5}>
    {tab === 'assess' &&
      ASSESS_ROWS.map((row) => (
        <ClaimRow
          key={row.id}
          icon={row.icon}
          name={row.name}
          description={row.description}
          // A count where there is work left to do, what the row offers
          // where there is not. The care profile is already filled in.
          count={
            row.action ??
            (row.answered !== undefined && row.total !== undefined
              ? `${row.answered} of ${row.total} answered`
              : undefined)
          }
          lead={row.lead}
          onClick={onCard}
        />
      ))}

    {tab === 'about' && <FactList groups={CARE_PROFILE.groups} confirmed />}

    {tab === 'docs' &&
      PROFILE_DOCS.map((doc) => (
        <RecordRow
          key={doc.id}
          title={doc.name}
          subtitle={`${doc.when} · ${doc.read}`}
        />
      ))}

    {tab === 'calls' &&
      PROFILE_CALLS.map((call) => (
        <RecordRow
          key={call.id}
          title={call.name}
          subtitle={`${call.when} · ${call.who}`}
        />
      ))}
  </Stack>
);

// PROPOSED. Everything about the person being cared for, in one tab.
//
// Assess is the work: the claims and the tools, each with how far in you
// already are. About is the record the intake filled in. Docs is what has been
// handed over, and Calls is what was said on one.
//
// Three pills where the master had four plus a separate Claims tab. The cards
// were on home in the last cut and it made home a menu; they belong with the
// person they are about.
const ArchitectureProfile = ({
  initialTab = 'assess',
  onNotifications,
  onCard,
}: {
  initialTab?: TabId;
  onNotifications?: () => void;
  /** An Assess row tap. Inert until each one has somewhere to go. */
  onCard?: () => void;
} = {}) => {
  const [tab, setTab] = useState<TabId>(initialTab);

  return (
    <ScreenRoot>
      <StatusBar />

      <Navigation
        title={CARE_PROFILE.person}
        onNotifications={onNotifications}
        avatarInitials='OP'
      />

      <Stack sx={{ px: SCREEN_PX, pt: 2, flexShrink: 0 }}>
        <ToggleButtonGroup
          className='segmented'
          exclusive
          fullWidth
          value={tab}
          onChange={(_, v) => v != null && setTab(v)}
        >
          {TABS.map((t) => (
            <ToggleButton key={t.id} value={t.id}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {/* A Box that scrolls, with the list inside it, rather than one Stack
          doing both. As direct children of a flex column that is `overflow:
          auto`, the cards are flex items: they shrink to fit the box instead of
          the box scrolling, so a full list quietly loses a few pixels off every
          card and the bottom padding goes with it. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: SCREEN_PX,
          pt: 2,
          pb: 2,
        }}
      >
        <ProfileSection tab={tab} onCard={onCard} />
      </Box>
    </ScreenRoot>
  );
};

export default ArchitectureProfile;
